import { useState } from 'react';
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { useToast } from '../../hooks/useToast';

const ROLE_LABELS: Record<string, string> = {
  root: 'Super Admin',
  admin: 'Admin',
  support: 'Support',
};

const ROLE_COLORS: Record<string, string> = {
  root: 'bg-purple-100 text-purple-700',
  admin: 'bg-indigo-100 text-indigo-700',
  support: 'bg-blue-100 text-blue-700',
};

export default function AdminAccount() {
  const { currentUser, userProfile, logout } = useAuth();
  const { showSuccess, showError } = useToast();

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const [savingName, setSavingName] = useState(false);
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [editingName, setEditingName] = useState(false);

  const role = userProfile?.role;

  async function handleSaveName() {
    if (!currentUser || !displayName.trim()) return;
    setSavingName(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        displayName: displayName.trim(),
        updatedAt: serverTimestamp(),
      });
      showSuccess('Name updated');
      setEditingName(false);
    } catch {
      showError('Failed to update name');
    } finally {
      setSavingName(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUser?.email) return;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      showError('New password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, passwordData.newPassword);
      showSuccess('Password updated successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        showError('Current password is incorrect');
      } else {
        showError('Failed to update password');
      }
    } finally {
      setChangingPassword(false);
    }
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-10">
      <h1 className="text-[22px] font-bold text-[#111827] mb-6">Account</h1>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm divide-y divide-[#F3F4F6]">

        {/* Role badge */}
        <div className="px-6 py-5 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-[#1A6B8A] flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
            {(userProfile?.displayName || currentUser?.email || 'A')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-[14px] font-semibold text-[#111827]">
              {userProfile?.displayName || currentUser?.email}
            </p>
            <p className="text-[12px] text-[#6B7280]">{currentUser?.email}</p>
          </div>
          {role && (
            <span className={`ml-auto px-2.5 py-0.5 rounded-full text-[11px] font-bold ${ROLE_COLORS[role] ?? 'bg-gray-100 text-gray-600'}`}>
              {ROLE_LABELS[role] ?? role}
            </span>
          )}
        </div>

        {/* Display name */}
        <div className="px-6 py-5">
          <p className="text-[11px] font-semibold text-[#6B7280] mb-2">DISPLAY NAME</p>
          {editingName ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                className="flex-1 px-3 py-2 border-[1.5px] border-[#E5E7EB] rounded-[9px] text-[13px] focus:outline-none focus:border-[#1A6B8A] input-glow"
              />
              <button
                onClick={handleSaveName}
                disabled={savingName}
                className="px-4 py-2 bg-[#1A6B8A] text-white text-[13px] font-bold rounded-[9px] hover:bg-[#0F4A63] transition-colors disabled:opacity-60 btn-press"
              >
                {savingName ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => { setEditingName(false); setDisplayName(userProfile?.displayName || ''); }}
                className="px-4 py-2 border-[1.5px] border-[#E5E7EB] text-[13px] font-semibold text-[#374151] rounded-[9px] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-[#111827]">{userProfile?.displayName || '-'}</span>
              <button
                onClick={() => setEditingName(true)}
                className="text-[12px] font-semibold text-[#1A6B8A] hover:underline"
              >
                Edit
              </button>
            </div>
          )}
        </div>

        {/* Change password */}
        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-semibold text-[#6B7280]">PASSWORD</p>
            <button
              onClick={() => setShowPasswordForm(v => !v)}
              className="text-[12px] font-semibold text-[#1A6B8A] hover:underline"
            >
              {showPasswordForm ? 'Cancel' : 'Change password'}
            </button>
          </div>

          {showPasswordForm && (
            <form onSubmit={handleChangePassword} className="mt-3 space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">CURRENT PASSWORD</label>
                <input
                  type="password"
                  required
                  value={passwordData.currentPassword}
                  onChange={e => setPasswordData(p => ({ ...p, currentPassword: e.target.value }))}
                  className="w-full px-3 py-2.5 border-[1.5px] border-[#E5E7EB] rounded-[9px] text-[13px] focus:outline-none focus:border-[#1A6B8A] input-glow"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">NEW PASSWORD</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={passwordData.newPassword}
                  onChange={e => setPasswordData(p => ({ ...p, newPassword: e.target.value }))}
                  className="w-full px-3 py-2.5 border-[1.5px] border-[#E5E7EB] rounded-[9px] text-[13px] focus:outline-none focus:border-[#1A6B8A] input-glow"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#6B7280] mb-1">CONFIRM NEW PASSWORD</label>
                <input
                  type="password"
                  required
                  value={passwordData.confirmPassword}
                  onChange={e => setPasswordData(p => ({ ...p, confirmPassword: e.target.value }))}
                  className="w-full px-3 py-2.5 border-[1.5px] border-[#E5E7EB] rounded-[9px] text-[13px] focus:outline-none focus:border-[#1A6B8A] input-glow"
                />
              </div>
              <button
                type="submit"
                disabled={changingPassword}
                className="w-full py-2.5 bg-[#1A6B8A] text-white text-[13px] font-bold rounded-[9px] hover:bg-[#0F4A63] transition-colors disabled:opacity-60 btn-press"
              >
                {changingPassword ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )}
        </div>

        {/* Logout */}
        <div className="px-6 py-5">
          <button
            onClick={logout}
            className="w-full py-2.5 border-[1.5px] border-red-200 text-red-600 text-[13px] font-bold rounded-[9px] hover:bg-red-50 transition-colors btn-press"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
